import { ReactElement } from "react";

import {
  InteractionObjectType,
  ProjectIssuanceConstants,
  ProjectIssuanceTransactionConstants,
} from "../../../../../../../constants";
import { GetProjectIssuancesResponse } from "../../../../../../../service/query";
import { isUnitKmHabitatType, TagsPanel } from "../../../../../../../templates";
import { formatNumber } from "../../../../../../../utils";
import {
  ActionButtons,
  Column,
  DatePicker,
  Divider,
  ErrorBox,
  FlyoutPanel,
  FormLabel,
  PillsList,
  ReadonlyText,
  Row,
  Select,
  TextInput,
} from "../../../../../../../widget";
import { useMultiIssuanceTransactionPanel } from "./useMultiIssuanceTransactionPanel";

interface MultiIssuanceTransactionPanelProps {
  issuances: GetProjectIssuancesResponse["results"];
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

export const MultiIssuanceTransactionPanel = ({
  issuances,
  show,
  onClose,
  handleRefresh,
}: MultiIssuanceTransactionPanelProps): JSX.Element => {
  const {
    errors,
    handleClosePanel,
    resetPanel,
    handleSubmit,
    isHandleSubmitLoading,
    multiIssuanceTransactionPanelData,
    setMultiIssuanceTransactionPanelData,
    tagPills,
    changedTagsExternal,
    setChangedTagsExternal,
    showTagsPanelIndex,
    setShowTagsPanelIndex,
  } = useMultiIssuanceTransactionPanel({
    show,
    issuances,
    onClose,
    handleRefresh,
  });

  const renderErrors = (): ReactElement | undefined => {
    return errors && errors.length > 0 ? (
      <Row spacingV="ll">
        <Column span={12}>
          <ErrorBox text={errors.map((error) => error.message)} />
        </Column>
      </Row>
    ) : undefined;
  };

  const content = (): ReactElement => {
    return (
      <form onSubmit={(e) => handleSubmit(e)}>
        <Row spacingV="2xl">
          <Column span={12}>
            <p className="body1">
              If any of these transactions completes or cancels a previous reservation or sale, please settle it against
              the existing transaction in the table to keep the inventory accurate.
            </p>
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <h2>Apply to all transactions</h2>
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={4}>
            <DatePicker
              label="Date"
              value={multiIssuanceTransactionPanelData[0]?.transactionDate ?? undefined}
              onChange={(value) =>
                setMultiIssuanceTransactionPanelData(() => [
                  ...multiIssuanceTransactionPanelData.map((data) => ({
                    ...data,
                    transactionDate: value ?? null,
                  })),
                ])
              }
            />
          </Column>
          <Column offset={1} span={4}>
            <TextInput
              label="Counterparty"
              name="Counterparty"
              value={multiIssuanceTransactionPanelData[0]?.counterParty}
              onChange={(value) =>
                setMultiIssuanceTransactionPanelData(() => [
                  ...multiIssuanceTransactionPanelData.map((data) => ({
                    ...data,
                    counterParty: value ?? null,
                  })),
                ])
              }
            />
          </Column>
        </Row>
        <Row spacingV="2xl">
          <Column span={4}>
            <Select
              label="Type"
              data={ProjectIssuanceTransactionConstants.ADD_ISSUANCE_TRANSACTION_TYPE_SELECT_DATA}
              value={multiIssuanceTransactionPanelData[0]?.type}
              onChange={(value) =>
                setMultiIssuanceTransactionPanelData(() => [
                  ...multiIssuanceTransactionPanelData.map((data) => ({
                    ...data,
                    type: value,
                  })),
                ])
              }
            />
          </Column>
          <Column offset={1} span={4}>
            <TextInput
              label="Broker"
              name="Broker"
              value={multiIssuanceTransactionPanelData[0]?.broker}
              onChange={(value) =>
                setMultiIssuanceTransactionPanelData(() => [
                  ...multiIssuanceTransactionPanelData.map((data) => ({
                    ...data,
                    broker: value ?? null,
                  })),
                ])
              }
            />
          </Column>
        </Row>
        <Row spacingV="2xl">
          <Column span={12}>
            <Divider type="horizontal" />
          </Column>
        </Row>
        {multiIssuanceTransactionPanelData.map((data, index) => (
          <div key={data.issuanceUuid}>
            <Row spacingV="ml">
              <Column span={12}>
                <p className="body01">
                  Issuance serial number:{" "}
                  {issuances.find((i) => i.uuid === data.issuanceUuid)?.serialNumber ?? (
                    <span className="NoData">Not available</span>
                  )}
                </p>
                <p className="body1">Issuance ID: {data.issuanceUuid}</p>
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={4}>
                <TextInput
                  label="Quantity"
                  name="Quantity"
                  type="number"
                  value={data.quantity?.toString()}
                  onChange={(value) =>
                    setMultiIssuanceTransactionPanelData(() => [
                      ...multiIssuanceTransactionPanelData.map((it, i) =>
                        i === index
                          ? {
                              ...it,
                              quantity: value === "" ? null : parseFloat(Number(value).toFixed(5)),
                              totalValue:
                                it.price && value !== ""
                                  ? parseFloat(Number(value).toFixed(5)) * it.price - (it.fees ?? 0)
                                  : null,
                            }
                          : it
                      ),
                    ])
                  }
                  minNumberValue={0}
                  maxNumberValue={999999.99}
                />
              </Column>
              <Column offset={1} span={4}>
                <ReadonlyText
                  label="Total value"
                  value={data.totalValue !== null ? `£${formatNumber(data.totalValue)}` : undefined}
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={4}>
                <TextInput
                  label="Price"
                  name="Price"
                  type="number"
                  prefix="£"
                  value={data.price?.toString()}
                  onChange={(value) =>
                    setMultiIssuanceTransactionPanelData(() => [
                      ...multiIssuanceTransactionPanelData.map((it, i) =>
                        i === index
                          ? {
                              ...it,
                              price: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                              totalValue:
                                it.quantity && value !== ""
                                  ? parseFloat(Number(value).toFixed(4)) * it.quantity - (it.fees ?? 0)
                                  : null,
                            }
                          : it
                      ),
                    ])
                  }
                  minNumberValue={0}
                  maxNumberValue={9999999999.99999}
                />
              </Column>
              <Column offset={1} span={4}>
                <TextInput
                  label="Transaction reference"
                  name="Transaction reference"
                  value={data.externalReference}
                  onChange={(value) =>
                    setMultiIssuanceTransactionPanelData(() => [
                      ...multiIssuanceTransactionPanelData.map((it, i) =>
                        i === index
                          ? {
                              ...it,
                              externalReference: value,
                            }
                          : it
                      ),
                    ])
                  }
                />
              </Column>
            </Row>
            <Row spacingV="ll">
              <Column span={4}>
                <TextInput
                  label="Fees"
                  name="Fees"
                  type="number"
                  prefix="£"
                  value={data.fees?.toString()}
                  onChange={(value) =>
                    setMultiIssuanceTransactionPanelData(() => [
                      ...multiIssuanceTransactionPanelData.map((it, i) =>
                        i === index
                          ? {
                              ...it,
                              fees: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                              totalValue:
                                it.quantity && it.price
                                  ? it.price * it.quantity - (value === "" ? 0 : parseFloat(Number(value).toFixed(4)))
                                  : null,
                            }
                          : it
                      ),
                    ])
                  }
                  minNumberValue={0}
                  maxNumberValue={9999999999.99999}
                />
              </Column>
              <Column offset={1} span={4}>
                <FormLabel label="Tags" />
                <PillsList
                  pills={tagPills[index] ?? []}
                  linkText="+ Add a tag"
                  linkOnClick={() => setShowTagsPanelIndex(index)}
                />
              </Column>
            </Row>
            {issuances.find((i) => i.uuid === data.issuanceUuid)?.unitMeasurement ===
              ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY && (
              <Row spacingV="ll">
                <Column span={4}>
                  <TextInput
                    label={`Unit size (${isUnitKmHabitatType(issuances.find((i) => i.uuid === data.issuanceUuid)?.habitatType ?? null) ? "Km" : "Ha"})`}
                    name="Unit size Ha / Km"
                    type="number"
                    value={data.areaHa?.toString()}
                    onChange={(value) =>
                      setMultiIssuanceTransactionPanelData(() => [
                        ...multiIssuanceTransactionPanelData.map((it, i) =>
                          i === index
                            ? {
                                ...it,
                                areaHa: value === "" ? null : Number(value),
                              }
                            : it
                        ),
                      ])
                    }
                    minNumberValue={0}
                  />
                </Column>
              </Row>
            )}
            {index !== multiIssuanceTransactionPanelData.length - 1 && (
              <Row spacingV="xl">
                <Column span={12}>
                  <Divider type="horizontal" />
                </Column>
              </Row>
            )}
          </div>
        ))}
        <Row spacingV="ll">
          <Column span={12}>
            <ActionButtons
              submitButtonLabel={`Add ${multiIssuanceTransactionPanelData.length > 1 ? "transactions" : "transaction"}`}
              onCancel={resetPanel}
              isLoading={isHandleSubmitLoading}
            />
          </Column>
        </Row>
      </form>
    );
  };

  return (
    <FlyoutPanel
      show={show}
      onClose={handleClosePanel}
      size="Large"
      contentChildren={
        <>
          {renderErrors()}
          {content()}
          {showTagsPanelIndex !== null && (
            <TagsPanel
              objectUuid={undefined}
              objectType={InteractionObjectType.ISSUANCE_TRANSACTION}
              show={showTagsPanelIndex !== null}
              onClose={() => setShowTagsPanelIndex(null)}
              shouldRefreshTags={() => undefined}
              changedTagsExternal={changedTagsExternal[showTagsPanelIndex] ?? []}
              setChangedTagsExternal={setChangedTagsExternal(showTagsPanelIndex)}
            />
          )}
        </>
      }
      header="Add transactions"
      buttonsChildren={undefined}
      height="Full"
    />
  );
};
